import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'

import {
  buildNodeServicePermissionOptions,
  mergeNodeServicePermissionOptions,
  resolveNodeServicePermissionOption,
  type NodeServicePermissionOption,
} from '@/lib/node-role-services'
import { orgTreeApi, type OrgTreeServiceInstance } from '@/services/org-tree'

type UseNodeLinkedServiceInstancesOptions = {
  nodeId?: string | number | null
  nodeIds?: Array<string | number | null | undefined>
  activeServiceInstanceId?: string | null
  enabled?: boolean
  supplementalOptions?: NodeServicePermissionOption[]
}

function normalizeNodeIds(
  nodeId?: string | number | null,
  nodeIds?: Array<string | number | null | undefined>,
): string[] {
  return Array.from(new Set(
    [
      nodeId,
      ...(nodeIds ?? []),
    ]
      .map((value) => String(value ?? '').trim())
      .filter(Boolean),
  ))
}

function mergeServiceInstances(rows: OrgTreeServiceInstance[]): OrgTreeServiceInstance[] {
  const byKey = new Map<string, OrgTreeServiceInstance>()

  for (const row of rows) {
    const instanceKey = String(row.instance_id ?? row.id ?? '').trim()
    if (!instanceKey || byKey.has(instanceKey)) {
      continue
    }
    byKey.set(instanceKey, row)
  }

  return Array.from(byKey.values())
}

async function fetchNodeLinkedServiceInstances(
  nodeId: string,
): Promise<OrgTreeServiceInstance[]> {
  return orgTreeApi.listServiceInstancesWithNode(nodeId, { includeInactive: true, lightweight: true })
}

export function useNodeLinkedServiceInstances({
  nodeId,
  nodeIds,
  activeServiceInstanceId,
  enabled = true,
  supplementalOptions = [],
}: UseNodeLinkedServiceInstancesOptions) {
  const normalizedNodeIds = useMemo(
    () => normalizeNodeIds(nodeId, nodeIds),
    [nodeId, nodeIds],
  )
  const normalizedActiveServiceInstanceId = String(activeServiceInstanceId ?? '').trim()
  const queriesEnabled = enabled && normalizedNodeIds.length > 0

  const queries = useQueries({
    queries: normalizedNodeIds.map((resolvedNodeId) => ({
      queryKey: ['node-linked-service-instances', resolvedNodeId],
      queryFn: () => fetchNodeLinkedServiceInstances(resolvedNodeId),
      enabled: queriesEnabled,
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      placeholderData: (previous: OrgTreeServiceInstance[] | undefined) => previous,
      retry: false,
    })),
  })

  const instances = useMemo(
    () => mergeServiceInstances(queries.flatMap((query) => query.data ?? [])),
    [queries],
  )

  const serviceOptions = useMemo(
    () => mergeNodeServicePermissionOptions(
      buildNodeServicePermissionOptions(instances),
      supplementalOptions,
    ),
    [instances, supplementalOptions],
  )

  const selectedOption = useMemo(
    () => resolveNodeServicePermissionOption(serviceOptions, normalizedActiveServiceInstanceId),
    [normalizedActiveServiceInstanceId, serviceOptions],
  )

  const hasMultiple = useMemo(() => {
    if (serviceOptions.length > 1) {
      return true
    }

    const variantKeys = new Set(
      instances
        .map((instance) => String(
          instance.service_variant
          ?? instance.service_variant_type
          ?? instance.service_data?.variant_unique
          ?? '',
        ).trim())
        .filter(Boolean),
    )

    return variantKeys.size > 1
  }, [instances, serviceOptions.length])

  const isLoading = queries.some((query) => query.isLoading)
  const isFetching = queries.some((query) => query.isFetching)

  return {
    nodeIds: normalizedNodeIds,
    instances,
    serviceOptions,
    selectedOption,
    hasMultiple,
    isLoading,
    isFetching,
  }
}

export type { NodeServicePermissionOption }
